import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FeedbackLoadingSpinner } from '@/components';
import {
  PackageFormValues,
  PackageStockPreview,
} from '@/types/package';
import { getPackageStockPreview } from '@/services/api/packages';
import { compileCriteria } from '../packageForm';

/** Long enough that typing "15000" is one request, short enough to feel live. */
const DEBOUNCE_MS = 400;

interface PreviewState {
  status: 'idle' | 'loading' | 'ready' | 'error';
  data: PackageStockPreview | null;
}

const IDLE: PreviewState = { status: 'idle', data: null };

/**
 * Live count of the units the criteria currently in the form would match.
 *
 * The criteria are built with the same `compileCriteria` the save path uses,
 * so the number cannot drift from what actually gets persisted.
 */
const useStockPreview = (values: PackageFormValues): PreviewState => {
  const [state, setState] = useState<PreviewState>(IDLE);

  /**
   * Monotonic request id. Only the newest request is allowed to write state,
   * so a slow response for older criteria cannot land on top of a fresher
   * result once it finally arrives.
   */
  const latestRequestRef = useRef(0);

  const modelID = values.modelID;

  /**
   * Serialised first, then revived, so the object's identity only changes when
   * the criteria themselves do: editing the package name or adding a
   * still-empty condition row leaves this untouched and spends no request.
   */
  const criteriaKey = useMemo(
    () => JSON.stringify(compileCriteria(values)),
    [values]
  );

  useEffect(() => {
    // Claim the slot immediately: any response still in flight for the
    // previous criteria is now stale, even before the new one is sent.
    latestRequestRef.current += 1;
    const requestId = latestRequestRef.current;

    if (!modelID) {
      setState(IDLE);
      return;
    }

    setState((prev) => ({ status: 'loading', data: prev.data }));

    const timer = setTimeout(() => {
      // Revived here rather than in a second memo: the serialised form is
      // what stabilises identity for the deps, and the object is only ever
      // needed at the moment the request actually fires.
      getPackageStockPreview({ modelID, criteria: JSON.parse(criteriaKey) })
        .then((data) => {
          if (latestRequestRef.current !== requestId) return;
          setState({ status: 'ready', data });
        })
        .catch(() => {
          // Informational only — never a toast, and never blocks saving.
          if (latestRequestRef.current !== requestId) return;
          setState({ status: 'error', data: null });
        });
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [modelID, criteriaKey]);

  return state;
};

/** Callers own the margin. */
export const noteClass = (tone: 'neutral' | 'warning') =>
  `flex items-start gap-2 rounded-lg border px-3 py-2.5 text-[13px] ${
    tone === 'warning'
      ? 'border-orange-200 bg-orange-50 text-orange-700'
      : 'border-gray-200 bg-gray-50 text-gray-600'
  }`;

interface PackageStockPreviewNoteProps {
  values: PackageFormValues;
  /** Opens the units behind the count, judged by these same criteria. */
  onViewUnits?: () => void;
  className?: string;
}

const ViewUnitsLink: React.FC<{ onClick?: () => void }> = ({ onClick }) =>
  onClick ? (
    <>
      {' '}
      <button
        type="button"
        onClick={onClick}
        className="font-medium text-primary-600 underline underline-offset-2 hover:text-primary-700"
      >
        Lihat unit
      </button>
    </>
  ) : null;

/**
 * Renders how many units of the selected model the current condition rows
 * would match. Zero is called out loudly — it means the package would be
 * published with nothing bookable behind it.
 */
export const PackageStockPreviewNote: React.FC<
  PackageStockPreviewNoteProps
> = ({ values, onViewUnits, className = '' }) => {
  const { status, data } = useStockPreview(values);

  if (status === 'idle') return null;

  if (status === 'error') {
    // The count is advisory, so a failure neutralises it rather than shouting.
    return (
      <div className={`mt-3 ${noteClass('neutral')} ${className}`}>
        <i className="ri-information-line text-[14px] leading-[20px]"></i>
        <span>Jumlah unit yang cocok belum bisa dihitung saat ini.</span>
      </div>
    );
  }

  if (!data) {
    return (
      <div className={`mt-3 ${noteClass('neutral')} ${className}`}>
        <FeedbackLoadingSpinner size="small" />
        <span>Menghitung unit yang cocok...</span>
      </div>
    );
  }

  const isStale = status === 'loading';
  const hasNoStock = data.available === 0;

  if (hasNoStock) {
    return (
      <div
        className={`mt-3 ${noteClass('warning')} ${isStale ? 'opacity-60' : ''} ${className}`}
      >
        <i className="ri-error-warning-line text-[14px] leading-[20px]"></i>
        <span>
          {data.totalOnModel === 0 ? (
            <>
              <strong className="font-medium">
                Model ini belum punya unit tersedia.
              </strong>{' '}
              Package akan tayang tanpa stok yang bisa dipesan.
            </>
          ) : (
            <>
              <strong className="font-medium">
                0 dari {data.totalOnModel} unit
              </strong>{' '}
              cocok dengan condition ini — package tidak akan punya stok yang
              bisa dipesan.
              <ViewUnitsLink onClick={onViewUnits} />
            </>
          )}
        </span>
      </div>
    );
  }

  return (
    <div
      className={`mt-3 ${noteClass('neutral')} ${isStale ? 'opacity-60' : ''} ${className}`}
    >
      <i className="ri-checkbox-circle-line text-[14px] leading-[20px]"></i>
      <span>
        <strong className="font-medium text-gray-900">
          {data.available} dari {data.totalOnModel} unit
        </strong>{' '}
        cocok dengan condition ini.
        <ViewUnitsLink onClick={onViewUnits} />
      </span>
    </div>
  );
};

export default PackageStockPreviewNote;
